import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ContentChild, ElementRef, HostListener, effect, forwardRef, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-editable-control',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './editable-control.component.html',
  styleUrls: ['./editable-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditableControlComponent),
      multi: true
    }
  ]
})
export class EditableControlComponent<T = unknown> implements ControlValueAccessor {
  readonly minHeight = input<number>(24);
  readonly editMode = input<boolean>(false);
  readonly allowIndividualEditMode = input<boolean>(false);
  // Operates on the already-stringified display value, not the raw T value
  // (see displayValue below) — hence string -> string, not T -> string.
  readonly displayTransform = input<(value: string) => string>();
  readonly externalValue = input<T | undefined>();

  @ContentChild('editInput', { static: false }) editInputRef?: ElementRef<HTMLInputElement>;

  public isEditMode = signal(false);
  public showEditButton = false;

  private _value: T | null = null;
  private _originalValue: T | null = null;
  public disabled = false;
  private _blockChangeNotification = false;

  onChange: (value: T | null) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef<HTMLElement>, private cd: ChangeDetectorRef) {
    effect(() => {
      if (this.editMode() && !this.isEditMode()) this.enterEditMode();
      else if (!this.editMode() && this.isEditMode()) this.exitEditMode(false);
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.allowIndividualEditMode() && this.isEditMode() && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.exitEditMode(true);
    }
  }

  get value(): T | null {
    return this._value;
  }

  set value(val: T | null) {
    const normalizedVal = val ?? null;
    if (this._value !== normalizedVal) {
      this._value = normalizedVal;
      if (!this._blockChangeNotification) {
        this.onChange(normalizedVal);
      }
    }
  }

  get displayValue(): string {
    const val = this.externalValue() ?? this._value;
    const raw = val != null ? String(val) : '';
    const transform = this.displayTransform();
    return transform ? transform(raw) : raw;
  }

  public toggleEditButton(value: boolean): void {
    this.showEditButton = value;
  }

  public enterEditMode(): void {
    this._originalValue = this._value ?? null;
    this._blockChangeNotification = true;
    setTimeout(() => {
      this.isEditMode.set(true);
      if (this.allowIndividualEditMode()) {
        setTimeout(() => {
          const inputEl = this.elementRef.nativeElement.querySelector<HTMLInputElement>('input');
          if (inputEl) {
            inputEl.focus();
          }
          this._blockChangeNotification = false;
        }, 0);
      } else {
        setTimeout(() => {
          this._blockChangeNotification = false;
        }, 0);
      }
    }, 0);
  }

  public exitEditMode(save: boolean): void {
    this._blockChangeNotification = true;
    if (!save) {
      const normalizedCurrent = this._value ?? null;
      const normalizedOriginal = this._originalValue ?? null;
      const valueChanged = normalizedCurrent !== normalizedOriginal;
      this._value = this._originalValue;
      if (valueChanged) {
        this.onChange(this._value);
      }
    }
    setTimeout(() => {
      this.isEditMode.set(false);
      setTimeout(() => {
        this._blockChangeNotification = false;
      }, 0);
    }, 0);
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (this.allowIndividualEditMode()) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.exitEditMode(true);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.exitEditMode(false);
      }
    }
  }

  // ControlValueAccessor methods
  public writeValue(value: T | null): void {
    this._value = value ?? null;
  }

  public registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}